// Emisión de Notas de Crédito (codDoc 04). Financiero → Gestión CxC → Emitir Nota de Crédito.
// numDocModificado se ingresa manual; al buscarlo se precargan las líneas de la
// factura. Reutiliza el pipeline de emisión SRI (fe_emitir → worker → XML/RIDE).
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Search, Trash2, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { DescartarDialog, type DocDescartable } from './DescartarDialog';
import { fechaLocal } from '@/lib/utils/fecha';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const hoy = fechaLocal();
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

const ESTADO: Record<string, { label: string; cls: string }> = {
  por_emitir: { label: 'Por emitir', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  encolado: { label: 'Encolado', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  autorizado: { label: 'Autorizado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  devuelto: { label: 'Devuelto', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const EMITIBLE = ['por_emitir', 'rechazado', 'devuelto', 'error'];

interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface Linea { key: number; codigo: string; descripcion: string; cantidad: number; precio_unitario: number; descuento: number; iva_tarifa: number }
let _k = 1;

export function NotasCredito() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'nueva' | 'lista'>('nueva');
  const [editId, setEditId] = useState<string | null>(null);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Emitir Nota de Crédito</h1>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nueva', editId ? 'Editar' : 'Nueva'], ['lista', 'Notas de crédito']] as const).map(([k, t]) => (
            <button key={k} onClick={() => { if (k === 'nueva' && vista === 'lista') setEditId(null); setVista(k); }}
              className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nueva'
        ? <Nueva editId={editId} onCreada={() => { setEditId(null); setVista('lista'); void qc.invalidateQueries({ queryKey: ['fac-nc'] }); }} />
        : <Lista onEdit={(id) => { setEditId(id); setVista('nueva'); }} />}
    </div>
  );
}

function Nueva({ editId, onCreada }: { editId: string | null; onCreada: () => void }) {
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  const clientes = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('cliente')), [partners]);
  const centros = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('centro_costo')), [partners]);

  const [clienteId, setClienteId] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [numDoc, setNumDoc] = useState('');
  const [fechaSustento, setFechaSustento] = useState('');
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([]);
  const setLinea = (key: number, patch: Partial<Linea>) => setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  // Modo edición: cargar la NC y precargar el formulario
  const { data: edit } = useQuery({
    queryKey: ['fac-obtener', editId], enabled: !!editId,
    queryFn: async () => (await apiClient.post('/rpc/fac_obtener', { _ticket_id: editId }, FIN)).data,
  });
  useEffect(() => {
    if (!edit) return;
    setClienteId(edit.partner_id || ''); setProyectoId(edit.proyecto_id || ''); setCentroId(edit.centro_costo_id || '');
    setNumDoc(edit.num_doc_modificado || ''); setFechaSustento(edit.fecha_doc_sustento ? String(edit.fecha_doc_sustento).slice(0, 10) : '');
    setMotivo(edit.motivo || ''); setObs(edit.observaciones || '');
    setLineas((edit.lineas || []).map((l: { codigo?: string; descripcion?: string; cantidad?: number; precio_unitario?: number; descuento?: number; iva_tarifa?: number }) => ({
      key: _k++, codigo: l.codigo || '', descripcion: l.descripcion || '', cantidad: Number(l.cantidad) || 0,
      precio_unitario: Number(l.precio_unitario) || 0, descuento: Number(l.descuento) || 0, iva_tarifa: Number(l.iva_tarifa) || 0,
    })));
  }, [edit]);

  // Buscar la factura por serie y precargar fecha sustento, cliente y líneas
  const buscar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/fac_buscar_factura_emitida', { _serie: numDoc.trim() }, FIN)).data,
    onSuccess: (f: any) => {
      if (!f) { toast.error('No se encontró una factura emitida con esa serie'); return; }
      setFechaSustento(String(f.fecha_emision).slice(0, 10));
      if (f.partner_id) setClienteId(f.partner_id);
      setLineas((f.lineas || []).map((l: any) => ({
        key: _k++, codigo: l.codigo || '', descripcion: l.descripcion || '', cantidad: Number(l.cantidad) || 0,
        precio_unitario: Number(l.precio_unitario) || 0, descuento: Number(l.descuento) || 0, iva_tarifa: Number(l.iva_tarifa) || 0,
      })));
      toast.success(`Factura ${f.serie} cargada (${(f.lineas || []).length} líneas)`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const base = (l: Linea) => l.cantidad * (l.precio_unitario - l.descuento);
  const totSub = lineas.reduce((s, l) => s + base(l), 0);
  const totDesc = lineas.reduce((s, l) => s + l.cantidad * l.descuento, 0);
  const totIva = lineas.reduce((s, l) => s + base(l) * (l.iva_tarifa || 0) / 100, 0);

  const crear = useMutation({
    mutationFn: async () => {
      const items = lineas.filter((l) => l.descripcion && l.cantidad > 0).map((l) => ({
        codigo: l.codigo, descripcion: l.descripcion, cantidad: l.cantidad, precio_unitario: l.precio_unitario,
        descuento: l.descuento, iva_tarifa: l.iva_tarifa, proyecto_id: proyectoId || null, centro_costo_id: centroId || null,
      }));
      if (items.length === 0) throw new Error('La nota de crédito no tiene líneas');
      const cab = { cliente_partner_id: clienteId, proyecto_id: proyectoId || null, centro_costo_id: centroId || null, num_doc_modificado: numDoc.trim(),
        fecha_doc_sustento: fechaSustento || null, motivo, observaciones: obs || null };
      return editId
        ? (await apiClient.post('/rpc/fac_actualizar', { _ticket_id: editId, _cab: cab, _items: items }, FIN)).data
        : (await apiClient.post('/rpc/fac_crear_nota_credito', { _cab: cab, _items: items }, FIN)).data;
    },
    onSuccess: () => { toast.success(editId ? 'Nota de crédito actualizada' : 'Nota de crédito creada'); onCreada(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Datos de cabecera</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3 rounded-md border border-primary/30 bg-primary/5 p-3">
            <label className="text-sm font-semibold">Factura que modifica (documento modificado) *</label>
            <p className="text-xs text-muted-foreground mb-1">Nº del comprobante de la factura a la que aplica esta NC (formato 001-101-000000123). Pulsa “Buscar” para traer sus líneas.</p>
            <div className="flex gap-2">
              <input className={field} placeholder="001-101-000000123" value={numDoc} onChange={(e) => setNumDoc(e.target.value)} />
              <Button size="sm" variant="outline" isLoading={buscar.isPending} disabled={!numDoc.trim()} onClick={() => buscar.mutate()}>
                <Search className="w-4 h-4 mr-1" /> Buscar
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Fecha del documento modificado *</label>
            <input type="date" className={field} value={fechaSustento} onChange={(e) => setFechaSustento(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Cliente *</label>
            <select className={field} value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.identificacion ?? 's/i'})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Fecha emisión</label>
            <input className={field} value={hoy} readOnly disabled />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Proyecto</label>
            <select className={field} value={proyectoId} onChange={(e) => setProyectoId(e.target.value)}>
              <option value="">—</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Centro de Costo *</label>
            <select className={field} value={centroId} onChange={(e) => setCentroId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {centros.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Motivo *</label>
            <input className={field} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. Devolución parcial, descuento, anulación…" />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">Observación</label>
            <input className={field} value={obs} onChange={(e) => setObs(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Detalle (líneas a acreditar)</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setLineas((l) => [...l, { key: _k++, codigo: '', descripcion: '', cantidad: 1, precio_unitario: 0, descuento: 0, iva_tarifa: 0 }])}><Plus className="w-4 h-4 mr-1" /> Línea</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1 pr-2">Código</th><th className="py-1 pr-2 min-w-48">Descripción</th><th className="py-1 pr-2">Cant.</th>
              <th className="py-1 pr-2">P. Unit.</th><th className="py-1 pr-2">Desc.</th><th className="py-1 pr-2">IVA%</th>
              <th className="py-1 pr-2 text-right">Subtotal</th><th></th>
            </tr></thead>
            <tbody>
              {lineas.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Busca la factura para precargar sus líneas, o agrega líneas manualmente</td></tr>}
              {lineas.map((l) => (
                <tr key={l.key} className="border-b last:border-0">
                  <td className="py-1 pr-2"><input className={`${field} w-24`} value={l.codigo} onChange={(e) => setLinea(l.key, { codigo: e.target.value })} /></td>
                  <td className="py-1 pr-2"><input className={field} value={l.descripcion} onChange={(e) => setLinea(l.key, { descripcion: e.target.value })} /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.01" className={`${field} w-20`} value={l.cantidad} onChange={(e) => setLinea(l.key, { cantidad: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.0001" className={`${field} w-24`} value={l.precio_unitario} onChange={(e) => setLinea(l.key, { precio_unitario: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.0001" className={`${field} w-20`} value={l.descuento} onChange={(e) => setLinea(l.key, { descuento: Number(e.target.value) })} /></td>
                  <td className="py-1 pr-2 text-muted-foreground whitespace-nowrap" title="IVA del producto (no editable)">{Number(l.iva_tarifa)}%</td>
                  <td className="py-1 pr-2 text-right whitespace-nowrap">{usd(base(l))}</td>
                  <td className="py-1"><button className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1" onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{usd(totSub)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Descuento</span><span>{usd(totDesc)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span>{usd(totIva)}</span></div>
              <div className="flex justify-between border-t pt-1 font-semibold"><span>Valor de modificación</span><span>{usd(totSub + totIva)}</span></div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={crear.isPending} disabled={!clienteId || !centroId || !numDoc.trim() || !motivo.trim() || !fechaSustento} onClick={() => crear.mutate()}>{editId ? 'Guardar cambios' : 'Generar nota de crédito'}</Button>
      </div>
    </div>
  );
}

interface NCRow {
  id: string; fecha_emision: string; cliente: string | null; num_doc_modificado: string | null; motivo: string | null;
  importe_total: number; lineas: number; estado_emision: string; mensaje_sri?: string | null; serie_comprobante?: string | null; xml_url?: string | null; ride_url?: string | null;
}

function Lista({ onEdit }: { onEdit: (id: string) => void }) {
  const qc = useQueryClient();
  const [descartar, setDescartar] = useState<DocDescartable | null>(null);
  const { data: docs = [], isFetching } = useQuery<NCRow[]>({
    queryKey: ['fac-nc'], queryFn: async () => (await apiClient.post('/rpc/fac_notas_credito', { _estado: 'todos' }, FIN)).data || [],
    refetchInterval: (q) => (q.state.data || []).some((d: NCRow) => d.estado_emision === 'encolado') ? 2500 : false,
  });
  const emitir = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/fe_emitir', { _ticket_id: id }, FIN)).data,
    onSuccess: () => { toast.success('Nota de crédito encolada'); void qc.invalidateQueries({ queryKey: ['fac-nc'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [cli, setCli] = useState('');
  const clientes = useMemo(() => ([...new Set(docs.map((d) => d.cliente).filter(Boolean))] as string[]).sort(), [docs]);
  const filtradas = useMemo(() => docs.filter((d) =>
    (!desde || d.fecha_emision >= desde) && (!hasta || d.fecha_emision <= hasta) && (!cli || d.cliente === cli)
  ), [docs, desde, hasta, cli]);

  const dlg = useDialogoEnLinea();

  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-2">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Notas de crédito ({filtradas.length})</CardTitle>
          <div className="flex items-end gap-2 flex-wrap text-xs text-muted-foreground">
            <label className="flex flex-col">Desde<input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="flex flex-col">Hasta<input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <label className="flex flex-col">Cliente
              <select className="h-9 mt-1 max-w-56 rounded-md border border-input bg-background px-2 text-sm" value={cli} onChange={(e) => setCli(e.target.value)}>
                <option value="">Todos</option>{clientes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            {(desde || hasta || cli) && <Button size="sm" variant="outline" onClick={() => { setDesde(''); setHasta(''); setCli(''); }}>Limpiar</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
</div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">N° Documento</th><th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3">Modifica</th>
            <th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
          </tr></thead>
          <tbody>
            {filtradas.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin notas de crédito</td></tr>}
            {filtradas.map((d) => (
              <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 whitespace-nowrap">{d.fecha_emision}</td>
                <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.serie_comprobante || '—'}</td>
                <td className="py-2 pr-3"><span className="truncate max-w-56 inline-block align-middle">{d.cliente || '—'}</span></td>
                <td className="py-2 pr-3 font-mono text-xs" title={d.motivo || ''}>{d.num_doc_modificado || '—'}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(Number(d.importe_total))}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${(ESTADO[d.estado_emision] || ESTADO.por_emitir).cls}`}>{(ESTADO[d.estado_emision] || ESTADO.por_emitir).label}</span>
                  {['devuelto', 'rechazado', 'error'].includes(d.estado_emision) && d.mensaje_sri && (
                    <button type="button" onClick={() => void dlg.avisar('Mensaje del SRI', d.mensaje_sri!)} title="Ver motivo completo"
                      className="mt-1 block max-w-xs text-left text-xs text-red-600 hover:underline">
                      ⚠ {d.mensaje_sri.length > 140 ? d.mensaje_sri.slice(0, 140) + '…' : d.mensaje_sri}
                    </button>
                  )}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  {urlDoc(d.ride_url) && <a href={urlDoc(d.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><FileText className="w-4 h-4" /> RIDE</a>}
                  {urlDoc(d.xml_url) && <a href={urlDoc(d.xml_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><Download className="w-4 h-4" /> XML</a>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle" title="Editar" onClick={() => onEdit(d.id)}><Pencil className="w-4 h-4" /></button>}
                  {EMITIBLE.includes(d.estado_emision) && <Button size="sm" isLoading={emitir.isPending} onClick={() => emitir.mutate(d.id)}>Emitir</Button>}
                  {d.estado_emision === 'encolado' && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> procesando…</span>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle" title="Descartar" onClick={() => setDescartar({ id: d.id, serie_comprobante: d.serie_comprobante, estado_emision: d.estado_emision })}><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {descartar && <DescartarDialog doc={descartar} onClose={() => setDescartar(null)}
          onDone={() => void qc.invalidateQueries({ queryKey: ['fac-nc'] })} />}
      </CardContent>
    </Card>
  );
}
